/* eslint-disable no-restricted-syntax */
import {
    addDoc,
    arrayUnion,
    collection,
    deleteDoc,
    deleteField,
    doc,
    DocumentData,
    getDoc,
    getDocs,
    orderBy,
    query,
    setDoc,
    Timestamp,
    updateDoc,
    where,
    writeBatch,
} from 'firebase/firestore';

import { isEqual } from 'lodash';

import { ICalendarEvent } from '@customTypes/calendar';
import {
    GroupNewEditFormValues,
    IGroup,
    IGroupDailySheet,
    IGroupPoints,
    IGroupSchedule,
    ISchedule,
    IStudentAttendance,
    IStudentPoints,
    IStudentPointsBalanceSheet,
    MeetingLinksByDay,
    Weekday,
} from '@customTypes/group';
import { IUser } from '@customTypes/user';
import { CalendarCreateEditFormValues } from 'components/ScheduleCalendar/calendar/EventDialog';
import { db } from 'external/firebase/index';
import { DailySheetReportFormValues } from 'pages/dashboard/attendance/DailySheet/DailySheet';

import { fDate, isDateLessThanOrEqual } from 'utils/formatTime';
import { calculateValidPointsForRewards, isDateInCurrentSchoolYear } from 'utils/pointsUtils';

import { deleteDiplomaCodesForStudents, generateDiplomaCodesForGroup } from './diplomaCodes';
import { regenerateGeneratedEventsForGroup } from './syncSchedule';
import { addGroupToUsersBatch, getUsersByIds, removeGroupFromUsersBatch } from './users';
import { findUserUpdates } from './utils';

const normalizeGroupData = (data: IGroup): IGroup => {
    const normalized = { ...data };

    if (data.meetingLink && !data.meetingLinks) {
        const days: Weekday[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        const links: MeetingLinksByDay = {};
        for (const day of days) {
            links[day] = [data.meetingLink!];
        }
        normalized.meetingLinks = links;
    }

    if (data.meetingLinks && !Array.isArray(data.meetingLinks)) {
        const newLinks: any = {};
        for (const [day, links] of Object.entries(data.meetingLinks)) {
            newLinks[day] = typeof links === 'string' ? [links] : links;
        }
        normalized.meetingLinks = newLinks;
    }

    return normalized;
};

const normalizeGroupSchedule = (data: IGroupSchedule): IGroupSchedule => {
    const normalized = { ...data };
    if ((data as any).meetingLink && !data.meetingLinks) {
        normalized.meetingLinks = [(data as any).meetingLink];
    }
    if (data.meetingLinks && !Array.isArray(data.meetingLinks)) {
        normalized.meetingLinks = [data.meetingLinks as any];
    }
    return normalized;
};

export const getGroup = async (id: string) => {
    const docRef = doc(db, 'groups', id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
        throw new Error('db/no-document');
    }
    return normalizeGroupData({ id, ...docSnap.data() } as IGroup);
};

export const getGroupByProductId = async (productId: string) => {
    const groups: DocumentData[] = [];
    const q = query(collection(db, 'groups'), where('product', '==', productId));
    const querySnap = await getDocs(q);
    querySnap.forEach(docSnap => {
        groups.push(normalizeGroupData({ id: docSnap.id, ...docSnap.data() } as IGroup));
    });
    return groups[0] as IGroup;
};

// TODO: maybe move to users.ts
export const getGroupStudents = async (id: string) => {
    const students: DocumentData[] = [];
    const q = query(collection(db, 'users'), where('role', '==', 'child'), where('groups', 'array-contains', id));
    const querySnap = await getDocs(q);
    querySnap.forEach(docSnap => {
        students.push({ id: docSnap.id, ...docSnap.data() });
    });
    return students as IUser[];
};

export const getUserGroups = async (id: string, role: IUser['role'] = 'child') => {
    const groups: DocumentData[] = [];
    const q = query(
        collection(db, 'groups'),
        where(role === 'child' ? 'students' : 'teachers', 'array-contains', { userId: id }),
    );
    const querySnap = await getDocs(q);
    querySnap.forEach(docSnap => {
        groups.push(normalizeGroupData({ id: docSnap.id, ...docSnap.data() } as IGroup));
    });
    return groups as IGroup[];
};

export const getAllGroupStudentAttendance = async (groupId: string) => {
    const attendanceRecords: { [studentId: string]: { [date: string]: IStudentAttendance } } = {};

    const q = query(collection(db, 'groups', groupId, 'dailySheet'));
    const querySnap = await getDocs(q);

    querySnap.forEach(docSnap => {
        const docData = docSnap.data();
        const attendanceDate = docSnap.id;

        if (docData.attendance && Array.isArray(docData.attendance)) {
            docData.attendance.forEach((attendance: any) => {
                const { userId, attendance: status, comment } = attendance;

                if (!attendanceRecords[userId]) {
                    attendanceRecords[userId] = {};
                }

                attendanceRecords[userId][attendanceDate] = {
                    attendance: status,
                    comment,
                    isApproved: docData?.isApproved || false,
                    groupComment: docData.comment,
                    userId,
                };
            });
        }
    });

    return attendanceRecords;
};

export const getGroupDailySheets = async (groupId: string): Promise<IGroupDailySheet[]> => {
    const dailySheetsQuery = query(collection(db, 'groups', groupId, 'dailySheet'));
    const dailySheetsSnapshot = await getDocs(dailySheetsQuery);

    return dailySheetsSnapshot.docs.map(docSnap => ({ date: docSnap.id, ...docSnap.data() } as IGroupDailySheet));
};

export const getGroupScheduleDateKeys = async (groupId: string): Promise<string[]> => {
    const scheduleQuery = query(collection(db, 'groups', groupId, 'schedule'));
    const scheduleSnapshot = await getDocs(scheduleQuery);

    return scheduleSnapshot.docs.map(docSnap => docSnap.id);
};

export const getGroupStudentAttendance = async (id: string, studentId: string) => {
    const attendanceRecords: { [key: string]: IStudentAttendance } = {};
    const q = query(collection(db, 'groups', id, 'dailySheet'));
    const querySnap = await getDocs(q);
    querySnap.forEach(docSnap => {
        const attendance = docSnap.data().attendance.find(a => a.userId === studentId);
        if (attendance) {
            attendanceRecords[docSnap.id] = {
                attendance: attendance.attendance,
                comment: attendance.comment,
                isApproved: docSnap.data()?.isApproved || false,
                groupComment: docSnap.data().comment,
                userId: studentId,
            };
        }
    });
    return attendanceRecords;
};

export const getGroups = async () => {
    const groups: DocumentData[] = [];
    const q = query(collection(db, 'groups'));
    const querySnap = await getDocs(q);
    querySnap.forEach(docSnap => {
        groups.push(normalizeGroupData({ id: docSnap.id, ...docSnap.data() } as IGroup));
    });
    return groups as IGroup[];
};

export const getGroupsByIds = async (groupIds: string[]) => {
    if (groupIds.length === 0) {
        return [];
    }

    if (groupIds.length <= 10) {
        const groups: DocumentData[] = [];
        const q = query(collection(db, 'groups'), where('__name__', 'in', groupIds));
        const querySnap = await getDocs(q);
        querySnap.forEach(docSnap => {
            groups.push(normalizeGroupData({ id: docSnap.id, ...docSnap.data() } as IGroup));
        });
        return groups as IGroup[];
    }

    const chunks: string[][] = [];
    for (let i = 0; i < groupIds.length; i += 10) {
        chunks.push(groupIds.slice(i, i + 10));
    }

    const chunkPromises = chunks.map(async (chunk: string[]) => {
        try {
            const groups: DocumentData[] = [];
            const q = query(collection(db, 'groups'), where('__name__', 'in', chunk));
            const querySnap = await getDocs(q);
            querySnap.forEach(docSnap => {
                groups.push({ id: docSnap.id, ...docSnap.data() });
            });
            return groups;
        } catch (error) {
            console.warn('Failed to fetch group chunk:', error);
            return [];
        }
    });

    const chunkResults = await Promise.all(chunkPromises);
    return chunkResults.flat() as IGroup[];
};

const getDayName = (date: Date): Weekday => {
    const days: Weekday[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[date.getDay()];
};

export const createGroupSchedule = async (groupId: string, group: GroupNewEditFormValues) => {
    if (!group.startDate || !group.endDate) {
        return;
    }

    const scheduleCollectionRef = collection(db, `groups/${groupId}/schedule`);

    const startDate = new Date(group.startDate);
    const endDate = new Date(group.endDate);

    if (endDate < startDate) {
        throw new Error('End date must be after start date');
    }

    const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

    for (const dayName of daysOfWeek) {
        if (group[`${dayName}Enabled`]) {
            const daySchedule = {
                startTime: group[`${dayName}StartTime`],
                endTime: group[`${dayName}EndTime`],
                repeats: group[`${dayName}Repeats`],
            };

            const dayOfWeekIndex = daysOfWeek.indexOf(dayName);

            for (const occurrence of getOccurrences(daySchedule, startDate, endDate, dayOfWeekIndex)) {
                const dayNameFromDate = getDayName(occurrence.start);
                const scheduleData = {
                    mode: group.mode,
                    address: group.address,
                    city: group.city,
                    instructions: group.instructions,
                    meetingLinks: group.meetingLinks?.[dayNameFromDate] || [],
                    meetingRecordings: group.meetingRecordings,
                    meetingInformation: group.meetingInformation,
                    start: occurrence.start,
                    end: occurrence.end,
                };

                // eslint-disable-next-line no-await-in-loop
                await setDoc(doc(scheduleCollectionRef, fDate(occurrence.start)), scheduleData);
            }
        }
    }
};

export const createGroup = async (group: GroupNewEditFormValues) => {
    const data = {
        address: group.address,
        city: group.city,
        instructions: group.instructions,
        meetingLinks: group.meetingLinks,
        meetingRecordings: group.meetingRecordings,
        meetingInformation: group.meetingInformation,
        maxStudents: group.maxStudents,
        name: group.name,
        product: group.product,
        status: group.status,
        mode: group.mode,
        students: group.students,
        teachers: group.teachers,
        name_for_diploma: group.name_for_diploma,
        description_for_diploma: group.description_for_diploma,
        learning_hours: group.learning_hours,
        diploma_enabled: group.diploma_enabled,
        feedback_enabled: group.feedback_enabled,
        space_odyssey_enabled: group.space_odyssey_enabled,
        schedule: {
            startDate: group.startDate,
            endDate: group.endDate,
            monday: group.mondayEnabled
                ? {
                      startTime: group.mondayStartTime,
                      endTime: group.mondayEndTime,
                      repeats: group.mondayRepeats,
                  }
                : null,
            tuesday: group.tuesdayEnabled
                ? {
                      startTime: group.tuesdayStartTime,
                      endTime: group.tuesdayEndTime,
                      repeats: group.tuesdayRepeats,
                  }
                : null,
            wednesday: group.wednesdayEnabled
                ? {
                      startTime: group.wednesdayStartTime,
                      endTime: group.wednesdayEndTime,
                      repeats: group.wednesdayRepeats,
                  }
                : null,
            thursday: group.thursdayEnabled
                ? {
                      startTime: group.thursdayStartTime,
                      endTime: group.thursdayEndTime,
                      repeats: group.thursdayRepeats,
                  }
                : null,
            friday: group.fridayEnabled
                ? {
                      startTime: group.fridayStartTime,
                      endTime: group.fridayEndTime,
                      repeats: group.fridayRepeats,
                  }
                : null,
            saturday: group.saturdayEnabled
                ? {
                      startTime: group.saturdayStartTime,
                      endTime: group.saturdayEndTime,
                      repeats: group.saturdayRepeats,
                  }
                : null,
            sunday: group.sundayEnabled
                ? {
                      startTime: group.sundayStartTime,
                      endTime: group.sundayEndTime,
                      repeats: group.sundayRepeats,
                  }
                : null,
        },
        modules: [],
    };

    let groupId: string;
    if (group.id) {
        await setDoc(doc(db, 'groups', group.id), data);
        groupId = group.id;
    } else {
        const docRef = await addDoc(collection(db, 'groups'), data);
        groupId = docRef.id;
    }

    await addGroupToUsersBatch([...group.students, ...group.teachers], groupId);

    await createGroupSchedule(groupId, group);

    const students = await getUsersByIds(group.students.map(s => s.userId));
    const groupData = { id: groupId, name: group.name } as IGroup;
    await generateDiplomaCodesForGroup(groupData, students);
};

function* getOccurrences(
    daySchedule: { startTime: Date | string | null; endTime: Date | string | null; repeats: string },
    startDate: Date,
    endDate: Date,
    dayOfWeek: number,
) {
    let { startTime, endTime } = daySchedule;
    const { repeats } = daySchedule;

    if (!startTime || !endTime) {
        return;
    }

    if (typeof startTime === 'string') {
        startTime = new Date(`1970-01-01T${startTime}:00Z`);
    }
    if (typeof endTime === 'string') {
        endTime = new Date(`1970-01-01T${endTime}:00Z`);
    }

    if (!(startTime instanceof Date) || Number.isNaN(startTime.getTime())) {
        throw new Error('Invalid startTime');
    }
    if (!(endTime instanceof Date) || Number.isNaN(endTime.getTime())) {
        throw new Error('Invalid endTime');
    }

    const currentStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const currentDayOfWeek = (currentStart.getDay() + 6) % 7;

    const daysToAdd = (dayOfWeek - currentDayOfWeek + 7) % 7;
    currentStart.setDate(currentStart.getDate() + daysToAdd);

    while (isDateLessThanOrEqual(currentStart, endDate)) {
        const start = new Date(currentStart);
        const end = new Date(currentStart);

        start.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
        end.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);

        yield { start, end };

        if (repeats === 'once') {
            break;
        }

        // eslint-disable-next-line no-nested-ternary
        const daysToIncrement = repeats === 'weekly' ? 7 : repeats === 'biweekly' ? 14 : 0;
        currentStart.setDate(currentStart.getDate() + daysToIncrement);
    }
}

const updateGroupScheduleSelectively = async (
    groupId: string,
    currentSchedule: ISchedule,
    newSchedule: any,
    group: GroupNewEditFormValues,
) => {
    const existingScheduleDocs = await getGroupSchedule(groupId);
    const existingDates = new Set(
        existingScheduleDocs.map(doc => (doc.start ? fDate(doc.start.toDate()) : '')).filter(date => date !== ''),
    );

    const newRecurringLessons = generateScheduleLessons(groupId, group);
    const newRecurringDates = new Set(newRecurringLessons.map(lesson => fDate(lesson.start)));

    const originalRecurringLessons = currentSchedule
        ? generateScheduleLessonsFromSchedule(groupId, currentSchedule)
        : [];
    const originalRecurringDates = new Set(originalRecurringLessons.map(lesson => fDate(lesson.start)));

    if (group.endDate) {
        const newEndDate = new Date(group.endDate);
        const lessonsToRemoveAfterEndDate = existingScheduleDocs.filter(
            doc => doc.start && new Date(doc.start.toDate()) > newEndDate,
        );

        const deletePromises = lessonsToRemoveAfterEndDate
            .filter(lesson => lesson.start)
            .map(lesson => deleteGroupScheduleEvent(groupId, fDate(lesson.start!.toDate())));
        await Promise.all(deletePromises);
    }

    const recurringLessonsToRemove = existingScheduleDocs.filter(doc => {
        if (!doc.start) return false;
        const dateStr = fDate(doc.start.toDate());
        return originalRecurringDates.has(dateStr) && !newRecurringDates.has(dateStr);
    });

    const deleteOldPromises = recurringLessonsToRemove
        .filter(lesson => lesson.start)
        .map(lesson => deleteGroupScheduleEvent(groupId, fDate(lesson.start!.toDate())));
    await Promise.all(deleteOldPromises);

    const lessonsToAdd = newRecurringLessons.filter(lesson => {
        const dateStr = fDate(lesson.start);
        return (
            !existingDates.has(dateStr) ||
            recurringLessonsToRemove.some(removed => removed.start && fDate(removed.start.toDate()) === dateStr)
        );
    });

    const addPromises = lessonsToAdd.map(lesson => {
        const dayName = getDayName(lesson.start);
        const scheduleData = {
            mode: group.mode,
            address: group.address,
            city: group.city,
            instructions: group.instructions,
            meetingLinks: group.meetingLinks?.[dayName] || [],
            meetingRecordings: group.meetingRecordings,
            meetingInformation: group.meetingInformation,
            start: lesson.start,
            end: lesson.end,
        };

        return setDoc(doc(db, `groups/${groupId}/schedule`, fDate(lesson.start)), scheduleData);
    });
    await Promise.all(addPromises);

    const recurringLessonsToUpdate = existingScheduleDocs.filter(doc => {
        if (!doc.start) return false;
        const dateStr = fDate(doc.start.toDate());
        return originalRecurringDates.has(dateStr) && newRecurringDates.has(dateStr);
    });

    const updatePromises = recurringLessonsToUpdate
        .filter(lesson => lesson.start && lesson.end)
        .map(lesson => {
            const lessonDate = lesson.start!.toDate();
            const dayName = getDayName(lessonDate);
            const updateData = {
                mode: group.mode,
                address: group.address,
                city: group.city,
                instructions: group.instructions,
                meetingLinks: group.meetingLinks?.[dayName] || [],
                meetingRecordings: group.meetingRecordings,
                meetingInformation: group.meetingInformation,

                start: lesson.start,
                end: lesson.end,
            };

            return setDoc(doc(db, `groups/${groupId}/schedule`, fDate(lessonDate)), updateData);
        });
    await Promise.all(updatePromises);
};

const generateScheduleLessons = (groupId: string, group: GroupNewEditFormValues) => {
    if (!group.startDate || !group.endDate) {
        return [];
    }

    const lessons: { start: Date; end: Date }[] = [];
    const startDate = new Date(group.startDate);
    const endDate = new Date(group.endDate);

    const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

    for (const dayName of daysOfWeek) {
        if (group[`${dayName}Enabled`]) {
            const daySchedule = {
                startTime: group[`${dayName}StartTime`],
                endTime: group[`${dayName}EndTime`],
                repeats: group[`${dayName}Repeats`],
            };

            const dayOfWeek = daysOfWeek.indexOf(dayName);

            for (const occurrence of getOccurrences(daySchedule, startDate, endDate, dayOfWeek)) {
                lessons.push(occurrence);
            }
        }
    }

    return lessons;
};

const generateScheduleLessonsFromSchedule = (groupId: string, schedule: ISchedule) => {
    if (!schedule.startDate || !schedule.endDate) {
        return [];
    }

    const lessons: { start: Date; end: Date }[] = [];
    const startDate = schedule.startDate.toDate();
    const endDate = schedule.endDate.toDate();

    const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

    for (const dayName of daysOfWeek) {
        const daySchedule = schedule[dayName];
        if (daySchedule && daySchedule.startTime && daySchedule.endTime) {
            const dayScheduleForGenerator = {
                startTime: daySchedule.startTime.toDate(),
                endTime: daySchedule.endTime.toDate(),
                repeats: daySchedule.repeats,
            };

            const dayOfWeek = daysOfWeek.indexOf(dayName);

            for (const occurrence of getOccurrences(dayScheduleForGenerator, startDate, endDate, dayOfWeek)) {
                lessons.push(occurrence);
            }
        }
    }

    return lessons;
};

export const updateGroup = async (group: GroupNewEditFormValues) => {
    const docRef = doc(db, 'groups', group.id);

    const querySnap = await getDoc(docRef);
    const currentGroup = querySnap.data() as Omit<IGroup, 'id'>;

    const { addedUsers: addedStudents, removedUsers: removedStudents } = findUserUpdates(
        currentGroup.students,
        group.students,
    );
    const { addedUsers: addedTeachers, removedUsers: removedTeachers } = findUserUpdates(
        currentGroup.teachers,
        group.teachers,
    );

    await addGroupToUsersBatch([...addedStudents, ...addedTeachers], group.id);
    await removeGroupFromUsersBatch([...removedStudents, ...removedTeachers], group.id);

    if (removedStudents.length > 0) {
        const removedStudentIds = removedStudents.map(student => student.userId);
        await deleteDiplomaCodesForStudents(removedStudentIds, group.id);
    }

    const newSchedule: ISchedule = {
        startDate: group.startDate ? Timestamp.fromDate(new Date(group.startDate)) : null,
        endDate: group.endDate ? Timestamp.fromDate(new Date(group.endDate)) : null,
    };

    // Only add enabled days to avoid undefined values
    if (group.mondayEnabled) {
        newSchedule.monday = {
            startTime: group.mondayStartTime ? Timestamp.fromDate(new Date(group.mondayStartTime)) : null,
            endTime: group.mondayEndTime ? Timestamp.fromDate(new Date(group.mondayEndTime)) : null,
            repeats: group.mondayRepeats,
        };
    }
    if (group.tuesdayEnabled) {
        newSchedule.tuesday = {
            startTime: group.tuesdayStartTime ? Timestamp.fromDate(new Date(group.tuesdayStartTime)) : null,
            endTime: group.tuesdayEndTime ? Timestamp.fromDate(new Date(group.tuesdayEndTime)) : null,
            repeats: group.tuesdayRepeats,
        };
    }
    if (group.wednesdayEnabled) {
        newSchedule.wednesday = {
            startTime: group.wednesdayStartTime ? Timestamp.fromDate(new Date(group.wednesdayStartTime)) : null,
            endTime: group.wednesdayEndTime ? Timestamp.fromDate(new Date(group.wednesdayEndTime)) : null,
            repeats: group.wednesdayRepeats,
        };
    }
    if (group.thursdayEnabled) {
        newSchedule.thursday = {
            startTime: group.thursdayStartTime ? Timestamp.fromDate(new Date(group.thursdayStartTime)) : null,
            endTime: group.thursdayEndTime ? Timestamp.fromDate(new Date(group.thursdayEndTime)) : null,
            repeats: group.thursdayRepeats,
        };
    }
    if (group.fridayEnabled) {
        newSchedule.friday = {
            startTime: group.fridayStartTime ? Timestamp.fromDate(new Date(group.fridayStartTime)) : null,
            endTime: group.fridayEndTime ? Timestamp.fromDate(new Date(group.fridayEndTime)) : null,
            repeats: group.fridayRepeats,
        };
    }
    if (group.saturdayEnabled) {
        newSchedule.saturday = {
            startTime: group.saturdayStartTime ? Timestamp.fromDate(new Date(group.saturdayStartTime)) : null,
            endTime: group.saturdayEndTime ? Timestamp.fromDate(new Date(group.saturdayEndTime)) : null,
            repeats: group.saturdayRepeats,
        };
    }
    if (group.sundayEnabled) {
        newSchedule.sunday = {
            startTime: group.sundayStartTime ? Timestamp.fromDate(new Date(group.sundayStartTime)) : null,
            endTime: group.sundayEndTime ? Timestamp.fromDate(new Date(group.sundayEndTime)) : null,
            repeats: group.sundayRepeats,
        };
    }

    await updateDoc(docRef, {
        address: group.address,
        city: group.city,
        instructions: group.instructions,
        meetingLinks: group.meetingLinks,
        meetingRecordings: group.meetingRecordings,
        maxStudents: group.maxStudents,
        name: group.name,
        product: group.product,
        status: group.status,
        mode: group.mode,
        students: group.students,
        teachers: group.teachers,
        schedule: newSchedule,
        meetingLink: deleteField(),
        meetingInformation: group.meetingInformation,
        name_for_diploma: group.name_for_diploma,
        description_for_diploma: group.description_for_diploma,
        learning_hours: group.learning_hours,
        diploma_enabled: group.diploma_enabled,
        feedback_enabled: group.feedback_enabled,
        space_odyssey_enabled: group.space_odyssey_enabled,
    });

    const scheduleChanged = !isEqual(currentGroup.schedule, newSchedule);
    const eventDataChanged =
        currentGroup.name !== group.name ||
        currentGroup.mode !== group.mode ||
        currentGroup.address !== group.address ||
        currentGroup.city !== group.city ||
        !isEqual(currentGroup.meetingLinks, group.meetingLinks) ||
        currentGroup.meetingRecordings !== group.meetingRecordings ||
        currentGroup.meetingInformation !== group.meetingInformation;

    if (scheduleChanged || eventDataChanged) {
        await updateGroupScheduleSelectively(group.id, currentGroup.schedule, newSchedule, group);
    }

    if (scheduleChanged || eventDataChanged) {
        try {
            await regenerateGeneratedEventsForGroup({
                ...group,
                id: group.id,
                schedule: newSchedule,
            });

            // Trigger global calendar refresh
            window.dispatchEvent(
                new CustomEvent('refreshCalendar', {
                    detail: { groupId: group.id },
                }),
            );
        } catch (error) {
            console.error('Failed to regenerate calendar events for group:', group.id, error);
        }
    }

    const students = await getUsersByIds(group.students.map(s => s.userId));
    const groupData = { id: group.id, name: group.name } as IGroup;
    await generateDiplomaCodesForGroup(groupData, students);
};

export const getGroupSchedule = async (groupId: string): Promise<IGroupSchedule[]> => {
    const scheduleCollectionRef = collection(db, `groups/${groupId}/schedule`);
    const scheduleDocs = await getDocs(scheduleCollectionRef);
    return scheduleDocs.docs.map(doc => normalizeGroupSchedule(doc.data() as IGroupSchedule));
};

export const createGroupScheduleEvent = async (schedule: CalendarCreateEditFormValues) => {
    const { mode, address, city, instructions, meetingLinks, meetingRecordings, meetingInformation, start, end } =
        schedule;

    const groupSchedule = {
        address,
        mode,
        city,
        instructions,
        meetingLinks,
        meetingRecordings,
        meetingInformation,
        start,
        end,
    };
    const docRef = doc(db, `groups/${schedule.groupId}/schedule`, fDate(schedule.start));
    await setDoc(docRef, groupSchedule);
};

export const updateGroupScheduleEvent = async (previousDate: Date | string, schedule: CalendarCreateEditFormValues) => {
    const {
        groupId,
        mode,
        address,
        city,
        instructions,
        meetingLinks,
        meetingRecordings,
        meetingInformation,
        start,
        end,
    } = schedule;

    const groupSchedule = {
        address,
        mode,
        city,
        instructions,
        meetingLinks,
        meetingRecordings,
        meetingInformation,
        start,
        end,
    };
    const newFormattedDate = fDate(schedule.start);
    const newScheduleDocRef = doc(db, `groups/${groupId}/schedule/${fDate(schedule.start)}`);
    const previousFormattedDate = fDate(previousDate);

    if (previousFormattedDate !== newFormattedDate) {
        const oldScheduleDocRef = doc(db, `groups/${groupId}/schedule/${previousFormattedDate}`);
        await deleteDoc(oldScheduleDocRef);
    }

    await setDoc(newScheduleDocRef, groupSchedule);
};

export const deleteGroupScheduleEvent = async (groupId: string, scheduleId: string) => {
    const docRef = doc(db, `groups/${groupId}/schedule`, scheduleId);
    await deleteDoc(docRef);
};

const deleteGroupSchedule = async (groupId: string) => {
    const scheduleCollectionRef = collection(db, `groups/${groupId}/schedule`);
    const scheduleDocs = await getDocs(scheduleCollectionRef);
    const batch = writeBatch(db);
    scheduleDocs.forEach(doc => {
        batch.delete(doc.ref);
    });
    await batch.commit();
};

export const addStudentToGroup = async (userId: string, id: string) => {
    const docRef = doc(db, 'groups', id);
    await updateDoc(docRef, {
        students: arrayUnion({ userId }),
    });
};

export const deleteGroup = async (id: string) => {
    const docRef = doc(db, 'groups', id);
    const docSnap = await getDoc(docRef);
    const group = { id: docSnap.id, ...docSnap.data() } as IGroup;
    await removeGroupFromUsersBatch([...group.students, ...group.teachers], group.id);

    const studentIds = group.students.map(student => student.userId);
    if (studentIds.length > 0) {
        await deleteDiplomaCodesForStudents(studentIds, group.id);
    }

    await deleteDoc(docRef);
};

export const deleteGroups = async (ids: string[]) => {
    const batch = writeBatch(db);
    // eslint-disable-next-line no-restricted-syntax
    for (const id of ids) {
        const docRef = doc(db, 'groups', id);
        // eslint-disable-next-line no-await-in-loop
        const docSnap = await getDoc(docRef);
        const group = { id: docSnap.id, ...docSnap.data() } as IGroup;
        // eslint-disable-next-line no-await-in-loop
        await removeGroupFromUsersBatch([...group.students, ...group.teachers], group.id);

        const studentIds = group.students.map(student => student.userId);
        if (studentIds.length > 0) {
            // eslint-disable-next-line no-await-in-loop
            await deleteDiplomaCodesForStudents(studentIds, group.id);
        }

        batch.delete(docRef);
    }
    await batch.commit();
};

export const updateGroupModules = async (group: string, modules: object) => {
    const docRef = doc(db, 'groups', group);
    // @ts-ignore
    await updateDoc(docRef, { modules });
};

export const updateGroupDiplomaVisibility = async (groupId: string, diplomaVisible: boolean) => {
    const docRef = doc(db, 'groups', groupId);
    await updateDoc(docRef, { diploma_visible: diplomaVisible });
};

export const getGroupPoints = async (group: string, date: string) => {
    const docRef = doc(db, 'groups', group, 'points', date);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
        return { date: docSnap.id, ...docSnap.data() } as IGroupPoints;
    }
    return { ok: false, code: 'db/no-document' } as const;
};

export const getUnmigratedClaimedRewards = async (
    groupId: string,
    studentId: string,
): Promise<{ pointsRequired: number; rewardName: string; date: string; claimedBy: string }[]> => {
    const studentDoc = await getDoc(doc(db, 'users', studentId));
    if (!studentDoc.exists()) {
        return [];
    }

    const studentData = studentDoc.data();
    const parentId = studentData.parent;

    if (!parentId) {
        return [];
    }

    const unmigratedRewards: { pointsRequired: number; rewardName: string; date: string; claimedBy: string }[] = [];

    try {
        const parentClaimedRewardsRef = collection(db, 'users', parentId, 'claimedRewards');
        const parentClaimedRewardsQuery = query(parentClaimedRewardsRef, where('groupId', '==', groupId));
        const parentClaimedRewardsSnap = await getDocs(parentClaimedRewardsQuery);

        parentClaimedRewardsSnap.forEach(docSnap => {
            const data = docSnap.data();

            if (!data.claimedBy && data.pointsRequired && data.rewardName) {
                unmigratedRewards.push({
                    pointsRequired: data.pointsRequired,
                    rewardName: data.rewardName,
                    date: data.date || '',
                    claimedBy: parentId,
                });
            }
        });
    } catch (error) {
        console.error('Error fetching unmigrated claimed rewards:', error);
    }

    return unmigratedRewards;
};

export const getStudentEarnedPointsOnly = async (groupId: string, studentId: string): Promise<number> => {
    let pointsSum = 0;
    const dailySheetRef = collection(db, 'groups', groupId, 'dailySheet');
    const dailySheetQuery = query(dailySheetRef);
    const dailySheetSnap = await getDocs(dailySheetQuery);

    dailySheetSnap.forEach(docSnap => {
        const data = docSnap.data() as IGroupDailySheet;

        if (!data?.isApproved) return;

        const pointsList: IStudentPoints[] = data.points || [];
        const points = pointsList.find(a => a.userId === studentId);
        if (points) {
            pointsSum +=
                (Number(points.for_questions) ? points.for_questions : 0) +
                (Number(points.for_attendance) ? points.for_attendance : 0) +
                (Number(points.for_ideas) ? points.for_ideas : 0) +
                (Number(points.for_practices) ? points.for_practices : 0) +
                (Number(points.for_helping_others) ? points.for_helping_others : 0) +
                (Number(points.for_challenges) ? points.for_challenges : 0) +
                (Number(points.extra_points) ? points.extra_points : 0);
        }
    });

    return pointsSum;
};

export const getGroupStudentPoints = async (groupId: string, studentId: string): Promise<number> => {
    let pointsSum = 0;
    const dailySheetRef = collection(db, 'groups', groupId, 'dailySheet');
    const dailySheetQuery = query(dailySheetRef);
    const dailySheetSnap = await getDocs(dailySheetQuery);

    dailySheetSnap.forEach(docSnap => {
        const data = docSnap.data() as IGroupDailySheet;

        if (!data?.isApproved) return;

        const pointsList: IStudentPoints[] = data.points || [];
        const points = pointsList.find(a => a.userId === studentId);
        if (points) {
            pointsSum +=
                (Number(points.for_questions) ? points.for_questions : 0) +
                (Number(points.for_attendance) ? points.for_attendance : 0) +
                (Number(points.for_ideas) ? points.for_ideas : 0) +
                (Number(points.for_practices) ? points.for_practices : 0) +
                (Number(points.for_helping_others) ? points.for_helping_others : 0) +
                (Number(points.for_challenges) ? points.for_challenges : 0) +
                (Number(points.extra_points) ? points.extra_points : 0);
        }
    });

    let claimedPointsSum = 0;
    const claimedRewardsRef = collection(db, 'users', studentId, 'claimedRewards');
    const claimedRewardsQuery = query(claimedRewardsRef, where('groupId', '==', groupId));
    const claimedRewardsSnap = await getDocs(claimedRewardsQuery);

    claimedRewardsSnap.forEach(doc => {
        const data = doc.data();
        if (data.pointsRequired) {
            claimedPointsSum += data.pointsRequired;
        }
    });

    const unmigratedRewards = await getUnmigratedClaimedRewards(groupId, studentId);
    unmigratedRewards.forEach(reward => {
        claimedPointsSum += reward.pointsRequired;
    });

    const netPoints = Math.max(0, pointsSum - claimedPointsSum);

    return netPoints;
};

export const getGroupStudentPointsBalanceSheet = async (
    groupId: string,
    studentId: string,
): Promise<IStudentPointsBalanceSheet[]> => {
    const studentSheets: IStudentPointsBalanceSheet[] = [];
    const dailySheetsQuery = query(collection(db, 'groups', groupId, 'dailySheet'));
    const dailySheetsSnapshot = await getDocs(dailySheetsQuery);

    dailySheetsSnapshot.forEach(docSnap => {
        const data = docSnap.data() as IGroupDailySheet;
        const studentPoints = data.points.find(p => p.userId === studentId);
        const studentAttendance = data.attendance.find(a => a.userId === studentId);

        if (studentPoints && studentAttendance && data?.isApproved) {
            const date = docSnap.id;
            studentSheets.push({
                date,
                points: studentPoints,
                attendance: studentAttendance,
                isClaimedReward: false,
            });
        }
    });

    const claimedRewardsRef = collection(db, 'users', studentId, 'claimedRewards');
    const claimedRewardsQuery = query(claimedRewardsRef, where('groupId', '==', groupId));
    const claimedRewardsSnapshot = await getDocs(claimedRewardsQuery);

    claimedRewardsSnapshot.forEach(docSnap => {
        const data = docSnap.data();

        let claimedDate: Date;
        if (data.date) {
            claimedDate = new Date(data.date);
        } else if (data.claimedAt) {
            claimedDate = data.claimedAt.toDate();
        } else {
            claimedDate = new Date();
        }

        const claimedAtString = claimedDate.toISOString().split('T')[0];
        const { pointsRequired } = data;
        const { rewardName } = data;

        studentSheets.push({
            date: claimedAtString,
            claimedReward: {
                rewardName,
                pointsRequired,
            },
            isClaimedReward: true,
        });
    });

    const unmigratedRewards = await getUnmigratedClaimedRewards(groupId, studentId);
    unmigratedRewards.forEach(reward => {
        const claimedDate = reward.date ? new Date(reward.date) : new Date();
        const claimedAtString = claimedDate.toISOString().split('T')[0];

        studentSheets.push({
            date: claimedAtString,
            claimedReward: {
                rewardName: `${reward.rewardName} (via parent)`,
                pointsRequired: reward.pointsRequired,
            },
            isClaimedReward: true,
        });
    });

    studentSheets.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB.getTime() - dateA.getTime();
    });

    return studentSheets;
};

export const getGroupStudentsPoints = async (id: string): Promise<{ name: string; points: number }[]> => {
    const students = await getGroupStudents(id);

    const pointsMap: { [userId: string]: number } = {};
    students.forEach(student => {
        pointsMap[student.id] = 0;
    });

    const groupSheetQuery = query(collection(db, 'groups', id, 'dailySheet'));
    const querySnapshot = await getDocs(groupSheetQuery);

    querySnapshot.forEach(docSnap => {
        const data = docSnap.data() as IGroupDailySheet;
        data.points.forEach(studentPoints => {
            if (!data.isApproved) return;
            if (!pointsMap[studentPoints.userId]) {
                pointsMap[studentPoints.userId] = 0;
            }

            const forQuestions = typeof studentPoints.for_questions === 'number' ? studentPoints.for_questions : 0;
            const forAttendance = typeof studentPoints.for_attendance === 'number' ? studentPoints.for_attendance : 0;
            const forIdeas = typeof studentPoints.for_ideas === 'number' ? studentPoints.for_ideas : 0;
            const forPractices = typeof studentPoints.for_practices === 'number' ? studentPoints.for_practices : 0;
            const forHelpingOthers =
                typeof studentPoints.for_helping_others === 'number' ? studentPoints.for_helping_others : 0;
            const forChallenges = typeof studentPoints.for_challenges === 'number' ? studentPoints.for_challenges : 0;
            const extraPoints = typeof studentPoints.extra_points === 'number' ? studentPoints.extra_points : 0;

            pointsMap[studentPoints.userId] +=
                forQuestions + forAttendance + forIdeas + forPractices + forHelpingOthers + forChallenges + extraPoints;
        });
    });

    const results: { name: string; points: number }[] = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const student of students) {
        const userName = `${student.firstName} ${student.lastName}`;
        results.push({ name: userName, points: pointsMap[student.id] });
    }

    results.sort((a, b) => b.points - a.points);

    return results;
};

export const recordDailySheet = async (group: string, date: string, points: DailySheetReportFormValues) => {
    const docRef = doc(db, 'groups', group, 'dailySheet', date);

    const existingDoc = await getDoc(docRef);
    const existingData = existingDoc.exists() ? (existingDoc.data() as DailySheetReportFormValues) : null;

    let preservedAttendance: any[] = [];
    let preservedPoints: any[] = [];

    if (existingData) {
        const currentStudentIds = new Set(points.attendance.map(att => att.userId));

        preservedAttendance = existingData.attendance?.filter(att => !currentStudentIds.has(att.userId)) || [];
        preservedPoints = existingData.points?.filter(pt => !currentStudentIds.has(pt.userId)) || [];
    }

    const mergedData = {
        ...points,
        attendance: [...points.attendance, ...preservedAttendance],
        points: [...points.points, ...preservedPoints],
    };

    await setDoc(docRef, mergedData);
};

export const checkIfExistsInSchedule = async (group: string, date: string) => {
    const docRef = doc(db, 'groups', group, 'schedule', date);
    const docSnap = await getDoc(docRef);
    return docSnap.exists();
};

export const getGroupDailySheet = async (group: string, date: string): Promise<IGroupDailySheet | undefined> => {
    const docRef = doc(db, 'groups', group, 'dailySheet', date);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
        return { date: docSnap.id, ...docSnap.data() } as IGroupDailySheet;
    }
    return undefined;
};

export const getGroupStudentsPointsBySchoolYear = async (
    groupId: string,
): Promise<{ name: string; points: number }[]> => {
    const students = await getGroupStudents(groupId);

    const studentPointsPromises = students.map(async student => {
        try {
            const balanceSheet = await getGroupStudentPointsBalanceSheet(groupId, student.id);
            const schoolYearPoints = calculateValidPointsForRewards(balanceSheet);
            const userName = `${student.firstName} ${student.lastName}`;
            return { name: userName, points: schoolYearPoints };
        } catch (err) {
            console.error(`Error getting points for student ${student.id}:`, err);
            const userName = `${student.firstName} ${student.lastName}`;
            return { name: userName, points: 0 };
        }
    });

    const results = await Promise.all(studentPointsPromises);

    results.sort((a, b) => b.points - a.points);

    return results;
};

export const getEventsByGroup = async (groupId: string): Promise<ICalendarEvent[]> => {
    const eventsRef = collection(db, `groups/${groupId}/schedule`);
    const eventsSnap = await getDocs(eventsRef);
    return eventsSnap.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
    })) as ICalendarEvent[];
};

export const deleteEventById = async (groupId: string, eventId: string): Promise<void> => {
    const docRef = doc(db, `groups/${groupId}/schedule`, eventId);
    await deleteDoc(docRef);
};

export const saveEventToFirestore = async (groupId: string, event: ICalendarEvent): Promise<void> => {
    const docRef = doc(db, `groups/${groupId}/schedule`, fDate(event.start));
    await setDoc(docRef, event);
};
